
import { fetchWithAuth } from '../utils/fetchWithAuth';

class AIComponentInteractionManager {
  constructor() {
    this.registeredComponents = new Map();
    this.enabledCapabilities = new Map();
    this.planContext = null;
  }

  // Register a component with its available capabilities
  registerComponent(componentId, capabilities, handlers) {
    this.registeredComponents.set(componentId, {
      capabilities,
      handlers,
      isActive: false
    });
    
    // Initialize all capabilities as enabled by default
    capabilities.forEach(capability => {
      this.enabledCapabilities.set(`${componentId}.${capability}`, true);
    });
    
    console.log(`🔧 Registered component: ${componentId} with ${capabilities.length} capabilities`);
  }

  // Set which component is currently active (in view)
  setActiveComponent(componentId) {
    // Deactivate all components
    this.registeredComponents.forEach((component, id) => {
      component.isActive = (id === componentId);
    });
    
    console.log(`🎯 Active component: ${componentId}`);
  }

  // Update plan context for AI operations
  updatePlanContext(planContext) {
    this.planContext = planContext;
  }

  // Check if a capability is enabled for AI interaction
  isCapabilityEnabled(componentId, capability) {
    const key = `${componentId}.${capability}`;
    return this.enabledCapabilities.get(key) === true;
  }

  // Toggle capability on/off
  toggleCapability(componentId, capability, enabled) {
    const key = `${componentId}.${capability}`;
    this.enabledCapabilities.set(key, enabled);
    console.log(`🔄 Capability ${key}: ${enabled ? 'ENABLED' : 'DISABLED'}`);
  }

  // Get available capabilities for current active component
  getAvailableCapabilities() {
    const activeComponent = Array.from(this.registeredComponents.entries())
      .find(([id, component]) => component.isActive);
    
    if (!activeComponent) {
      return [];
    }

    const [componentId, component] = activeComponent;
    
    return component.capabilities
      .filter(capability => this.isCapabilityEnabled(componentId, capability))
      .map(capability => ({
        id: capability,
        componentId,
        fullId: `${componentId}.${capability}`
      }));
  }

  // Execute an AI action on a component
  async executeAction(componentId, capability, actionData) {
    const component = this.registeredComponents.get(componentId);
    
    if (!component) {
      throw new Error(`Component ${componentId} not registered`);
    }

    if (!component.isActive) {
      throw new Error(`Component ${componentId} is not currently active`);
    }

    if (!this.isCapabilityEnabled(componentId, capability)) {
      throw new Error(`Capability ${capability} is disabled for ${componentId}`);
    }

    const handler = component.handlers[capability];
    if (!handler) {
      throw new Error(`No handler found for ${componentId}.${capability}`);
    }

    console.log(`🤖 Executing AI action: ${componentId}.${capability}`, actionData);
    
    try {
      const result = await handler(actionData, this.planContext);
      console.log(`✅ AI action completed: ${componentId}.${capability}`);
      return result;
    } catch (error) {
      console.error(`❌ AI action failed: ${componentId}.${capability}`, error);
      throw error;
    }
  }

  // Get capability context for AI agents (what they can do)
  getCapabilityContext() {
    const capabilities = this.getAvailableCapabilities();
    
    if (capabilities.length === 0) {
      return "No AI capabilities available in current view.";
    }

    const activeComponent = Array.from(this.registeredComponents.entries())
      .find(([id, component]) => component.isActive);
    
    const [componentId] = activeComponent;
    
    return `
CURRENT VIEW: ${componentId}

AVAILABLE AI CAPABILITIES:
${capabilities.map(cap => `- ${cap.id}: Can perform ${cap.id.replace(/_/g, ' ')} on ${componentId}`).join('\n')}

USAGE: To use these capabilities, specify the action like:
"add tools to research stage" → uses add_tools_to_stages capability
"remove program 2" → uses program_removal capability  
"change tool duration to 8 hours" → uses tool_duration_editing capability
`;
  }
}

// Create singleton instance
const aiComponentManager = new AIComponentInteractionManager();

export default aiComponentManager;
